import { useEffect, useRef, useState } from 'react';

const features = [
  {
    icon: 'ri-shield-check-fill',
    color: 'from-emerald-500 to-teal-500',
    bgLight: 'bg-emerald-50',
    textColor: 'text-emerald-700',
    borderColor: 'border-emerald-200',
    title: '0 鎖號保證',
    subtitle: '來源合法・非黑卡',
    desc: '所有儲值均透過合法管道進行，絕不使用盜刷或黑卡，帳號安全有保障，讓您安心下單不擔心被鎖號。',
    badge: '安全第一',
  },
  {
    icon: 'ri-timer-flash-fill',
    color: 'from-amber-500 to-orange-500',
    bgLight: 'bg-amber-50',
    textColor: 'text-amber-700',
    borderColor: 'border-amber-200',
    title: '10 分鐘速儲',
    subtitle: '人工極速處理',
    desc: '專業操作團隊 24 小時待命，一般訂單付款後 10 分鐘內完成儲值，急用也不怕，效率業界第一。',
    badge: '極速到帳',
  },
  {
    icon: 'ri-price-tag-3-fill',
    color: 'from-rose-500 to-pink-500',
    bgLight: 'bg-rose-50',
    textColor: 'text-rose-700',
    borderColor: 'border-rose-200',
    title: '全站自動 9 折',
    subtitle: '無需領券・下單即享',
    desc: '不需要輸入任何折扣碼，全站所有代儲商品一律享有 9 折優惠，省錢省力，每次下單都划算。',
    badge: '自動折扣',
  },
  {
    icon: 'ri-lock-password-fill',
    color: 'from-violet-500 to-indigo-500',
    bgLight: 'bg-violet-50',
    textColor: 'text-violet-700',
    borderColor: 'border-violet-200',
    title: '帳密閱後即焚',
    subtitle: '24 小時自動銷毀',
    desc: '訂單完成 24 小時後，系統自動遮蔽並刪除資料庫中的帳號密碼欄位，您的隱私絕對不外洩。',
    badge: '隱私保護',
  },
  {
    icon: 'ri-secure-payment-fill',
    color: 'from-sky-500 to-cyan-500',
    bgLight: 'bg-sky-50',
    textColor: 'text-sky-700',
    borderColor: 'border-sky-200',
    title: 'SSL 全程加密',
    subtitle: 'HTTPS 安全傳輸',
    desc: '全站採用 SSL/TLS 加密技術，帳號密碼傳輸過程全程保護，任何第三方均無法截取您的資料。',
    badge: '加密傳輸',
  },
  {
    icon: 'ri-customer-service-2-fill',
    color: 'from-teal-500 to-green-500',
    bgLight: 'bg-teal-50',
    textColor: 'text-teal-700',
    borderColor: 'border-teal-200',
    title: '24H 專人客服',
    subtitle: '即時溝通・快速回應',
    desc: '每筆訂單均配有專屬對話窗，操作人員與您即時溝通驗證問題，全程陪伴直到儲值成功為止。',
    badge: '全天候服務',
  },
];

export default function WhyChooseUs() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4">
        {/* Section Header */}
        <div className={`text-center mb-14 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-full text-emerald-700 text-sm font-semibold mb-4">
            <i className="ri-shield-star-fill text-base"></i>
            為什麼選擇我們
          </div>
          <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4 leading-tight">
            業界最高安全標準<br />
            <span className="bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">讓您放心代儲</span>
          </h2>
          <p className="text-gray-500 text-lg max-w-2xl mx-auto">
            我們以「安全、速度、誠信」為核心，提供最值得信賴的手遊代儲服務，每一筆訂單都是我們的承諾。
          </p>
        </div>

        {/* Feature Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((f, i) => (
            <div
              key={f.title}
              className={`group relative bg-white rounded-2xl border-2 ${f.borderColor} p-6 hover:shadow-xl transition-all duration-500 cursor-default overflow-hidden ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              {/* Background Glow */}
              <div className={`absolute inset-0 ${f.bgLight} opacity-0 group-hover:opacity-60 transition-opacity duration-300 rounded-2xl`}></div>

              <div className="relative z-10">
                {/* Icon + Badge */}
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-14 h-14 flex items-center justify-center rounded-2xl bg-gradient-to-br ${f.color} shadow-lg`}>
                    <i className={`${f.icon} text-2xl text-white`}></i>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${f.bgLight} ${f.textColor} border ${f.borderColor}`}>
                    {f.badge}
                  </span>
                </div>

                {/* Title */}
                <h3 className="text-xl font-extrabold text-gray-900 mb-1">{f.title}</h3>
                <p className={`text-sm font-semibold ${f.textColor} mb-3`}>{f.subtitle}</p>

                {/* Divider */}
                <div className={`w-10 h-1 rounded-full bg-gradient-to-r ${f.color} mb-4`}></div>

                {/* Description */}
                <p className="text-gray-600 text-sm leading-relaxed">{f.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA Banner */}
        <div className={`mt-14 bg-gradient-to-r from-[#1a1f5e] via-[#1e3a8a] to-[#2563eb] rounded-3xl p-8 md:p-10 text-white flex flex-col md:flex-row items-center justify-between gap-6 transition-all duration-700 delay-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="text-center md:text-left">
            <h3 className="text-2xl md:text-3xl font-extrabold mb-2">準備好了嗎？</h3>
            <p className="text-white/80 text-base">立即下單，享受安全、快速、優惠的代儲體驗</p>
          </div>
          <div className="flex flex-wrap gap-4 justify-center">
            <div className="flex items-center gap-2 px-5 py-3 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
              <i className="ri-shield-check-fill text-emerald-300 text-xl"></i>
              <span className="font-bold text-sm whitespace-nowrap">0 鎖號保證</span>
            </div>
            <div className="flex items-center gap-2 px-5 py-3 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
              <i className="ri-timer-flash-fill text-amber-300 text-xl"></i>
              <span className="font-bold text-sm whitespace-nowrap">10 分鐘速儲</span>
            </div>
            <div className="flex items-center gap-2 px-5 py-3 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
              <i className="ri-price-tag-3-fill text-rose-300 text-xl"></i>
              <span className="font-bold text-sm whitespace-nowrap">全站 9 折</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
